// Local in-app reminder scheduler.
// FCM handles push when the app is closed; this hook handles foreground/in-app
// reminders so the user always sees them when the tab is open.
//
// Schedules (per spec):
//   - 7am: morning ritual reminder (daily)
//   - 8pm: streak reminder if no routine logged today
//   - Sunday 9am: weekly summary (premium only)
//   - 10am: new AI insight ready (premium only)
import { useEffect } from "react";
import { toast } from "sonner";
import { supabase } from "../lib/supabase";

function ymd(d = new Date()) {
  return d.toISOString().slice(0, 10);
}

// Real ISO week number (Mon-Sun, Thursday rule). Returns 'YYYY-Wxx'.
function isoWeek(d = new Date()) {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  // Thursday of current week determines the year
  t.setUTCDate(t.getUTCDate() + 4 - (t.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((t - yearStart) / 86400000 + 1) / 7);
  return `${t.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

function fired(key) {
  return localStorage.getItem(`reminder:${key}`);
}
function markFired(key, val = ymd()) {
  localStorage.setItem(`reminder:${key}`, val);
}

function notify(title, body, url = "/app") {
  // Native OS notification when permission granted.
  if (typeof Notification !== "undefined" && Notification.permission === "granted") {
    try {
      const n = new Notification(title, {
        body,
        icon: "/brand/logo.png",
        badge: "/brand/logo.png",
      });
      n.onclick = () => {
        window.focus();
        window.location.href = url;
        n.close();
      };
    } catch {
      /* some browsers require ServiceWorkerRegistration.showNotification */
    }
  }
  // Always show an in-app toast as a fallback.
  toast(title, { description: body });
}

export function useReminders(profile) {
  useEffect(() => {
    if (!profile?.id) return;

    const tick = async () => {
      const now = new Date();
      const today = ymd(now);
      const h = now.getHours();
      const day = now.getDay(); // 0 = Sunday

      // 1) Morning ritual reminder — fires once between 7:00 and 11:59
      if (h >= 7 && h < 12 && fired("morning") !== today) {
        markFired("morning", today);
        notify(
          "✨ Time for your glow ritual",
          `Good morning ${profile.name || ""} — your AM routine is waiting.`,
          "/app/routines"
        );
      }

      // 2) Streak reminder — fires once between 20:00 and 23:59 if no log today
      if (h >= 20 && h < 24 && fired("streak") !== today) {
        const { data } = await supabase
          .from("progress_logs")
          .select("id", { count: "exact", head: true })
          .eq("user_id", profile.id)
          .eq("log_date", today);
        if (!data || data.length === 0) {
          markFired("streak", today);
          notify(
            "🔥 Keep your streak alive",
            "You haven't logged today's routine yet. Two minutes saves your streak.",
            "/app/progress"
          );
        }
      }

      // 3) Weekly summary — Sunday only, premium users, fires once per ISO week
      if (day === 0 && h >= 9 && profile.is_premium) {
        const weekKey = isoWeek(now);
        if (fired("weekly") !== weekKey) {
          markFired("weekly", weekKey);
          notify(
            "👑 Your weekly Glow summary",
            "Your weekly progress is ready — see your Glow Score.",
            "/app/progress"
          );
        }
      }

      // 4) Daily AI insight — premium only, fires once per day (after 10am)
      if (h >= 10 && profile.is_premium && fired("insight") !== today) {
        markFired("insight", today);
        notify(
          "🧠 Today's AI insight is ready",
          "Open your personalized skin + hair intel for today.",
          "/app/insights"
        );
      }
    };

    // Run immediately, then every minute.
    tick();
    const id = setInterval(tick, 60 * 1000);
    return () => clearInterval(id);
  }, [profile?.id, profile?.is_premium, profile?.name]);
}
